import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { EngagementDetail } from '@anytag/graphql';
import { TableV2 } from '@anytag/shared-atoms';
import { THEME } from '@anytag/shared-constants';
import { useSelectItems } from '@anytag/shared-hooks';
import { TableTitle } from '../../../shared';
import { useEngagementProposalTable } from '../../hooks';
import { formatEngagementProposalData } from '../../utils';
import { ProposalDerivedProps } from '../../../shared/types';
import { RejectInfluencersActionBar } from './RejectInfluencersActionBar';

export interface RejectedInfluencersTableProps
  extends Pick<ProposalDerivedProps, 'jdMode' | 'rejectedInfluencers' | 'formattedRejectedInfluencers'> {
  engagement: EngagementDetail;
  hasTrackingReferral?: boolean;
  hasInfluencerCostPermission?: boolean;
}

export const RejectedInfluencersTable = ({
  jdMode,
  engagement,
  rejectedInfluencers,
  hasTrackingReferral,
  formattedRejectedInfluencers,
  hasInfluencerCostPermission
}: RejectedInfluencersTableProps) => {
  const { t } = useTranslation();
  const {
    allItemIds: allInfluencerIds,
    selectedItemIds: selectedInfluencerIds,
    hasAllItemsSelected: hasAllInfluencersSelected,
    selectItem: selectInfluencer,
    setSelectedItemIds: setSelectedInfluencerIds,
    toggleSelectAllItems: toggleSelectAllInfluencers
  } = useSelectItems({ listItems: formattedRejectedInfluencers, key: 'influencerId' });

  useEffect(() => {
    setSelectedInfluencerIds([]);
  }, [rejectedInfluencers.length]);

  const handleSelectAllIds = () => {
    setSelectedInfluencerIds(allInfluencerIds);
  };

  const { tableData } = formatEngagementProposalData(rejectedInfluencers, jdMode);
  const { columns } = useEngagementProposalTable({
    jdMode,
    engagement,
    hasTrackingReferral,
    hasInfluencerCostPermission,
    onRowSelect: selectInfluencer,
    listSelectedIds: selectedInfluencerIds,
    selectButtonProps: { onClick: handleSelectAllIds }
  });

  return (
    <div css={{ borderTop: THEME.border.base }}>
      <TableTitle title={t('Rejected Influencers')} />

      <div css={{ position: 'relative' }}>
        {selectedInfluencerIds.length > 0 ? (
          <RejectInfluencersActionBar
            engagement={engagement}
            onSelectAll={handleSelectAllIds}
            hasAllSelected={hasAllInfluencersSelected}
            onToggleSelect={toggleSelectAllInfluencers}
            selectedInfluencerIds={selectedInfluencerIds}
            numberSelected={selectedInfluencerIds.length}
          />
        ) : null}

        <TableV2
          noTopBorderRadius
          data={tableData}
          columns={columns}
          rowHeight="62px"
          className="rejected-proposal-table"
          stickyHeader
        />
      </div>
    </div>
  );
};
